import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Quote } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolveImageUrl } from '../utils/resolveImageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mockHappyCustomers = [
  { id: 1, customerName: "Mike Johnson", vehicleInfo: "2022 Ford F-150 XLT", review: "Couldn't be happier with my purchase! The team at Big Chief Motors made the whole process smooth and stress-free.", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80", rating: 5 },
  { id: 2, customerName: "Sarah Williams", vehicleInfo: "2021 Jeep Wrangler", review: "Amazing experience from start to finish. They found exactly what I was looking for and the financing was quick and easy.", image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80", rating: 5 },
  { id: 3, customerName: "David Martinez", vehicleInfo: "2020 RAM 1500 Big Horn", review: "Best car buying experience I've ever had. No pressure, honest pricing, and they really know their trucks.", image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80", rating: 5 },
  { id: 4, customerName: "Jennifer Lee", vehicleInfo: "2022 Honda CR-V Touring", review: "The team went above and beyond to help me find the perfect family SUV. Great service and even better prices!", image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80", rating: 5 },
];

const getImageUrl = (url) => {
  if (!url) return '';
  return resolveImageUrl(url) || url;
};

const CustomerCard = ({ customer }) => (
  <div className="flex-shrink-0 w-[280px] sm:w-[320px] mr-5 bg-zinc-900 rounded-2xl ring-1 ring-zinc-800 shadow-sm overflow-hidden group hover:ring-zinc-700 hover:-translate-y-1 transition-all duration-300">
    {/* Square crop: customer photos are a mix of portrait and landscape */}
    <div className="relative aspect-square overflow-hidden">
      <img src={getImageUrl(customer.image)} alt={customer.customerName} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute top-3 right-3 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
        <Quote size={14} className="text-white" />
      </div>
      <div className="absolute bottom-3 left-3 right-3 text-white">
        {customer.customerName && <h3 className="text-base font-bold">{customer.customerName}</h3>}
        {customer.vehicleInfo && <p className="text-red-400 font-medium text-xs">{customer.vehicleInfo}</p>}
      </div>
    </div>
    <div className="p-4">
      <div className="flex gap-0.5 mb-2">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={13} className={i < customer.rating ? 'fill-red-500 text-red-500' : 'text-zinc-700'} />
        ))}
      </div>
      {customer.review && <p className="text-gray-300 text-sm leading-relaxed italic line-clamp-3">"{customer.review}"</p>}
    </div>
  </div>
);

const HappyCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const { settings } = useSiteSettings();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/happy-customers`);
        setCustomers(res.data?.length > 0 ? res.data : mockHappyCustomers);
      } catch { setCustomers(mockHappyCustomers); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading || customers.length === 0) return null;

  // 4 copies; scrolling -25% moves exactly one copy at any card width (mobile cards are narrower)
  const items = [...customers, ...customers, ...customers, ...customers];

  return (
    <section className="py-20 lg:py-24 bg-zinc-950 overflow-hidden" data-testid="happy-customers-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-red-600"></span>
            <span className="text-red-500 font-semibold text-xs uppercase tracking-[0.22em]">{settings.happyTagline || "Our Happy Customers"}</span>
            <span className="h-px w-8 bg-red-600"></span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white tracking-tight mb-4">{settings.happyTitle || "Driving Away Happy"}</h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {settings.happySubtitle || "See what our customers have to say about their experience at Big Chief Motors."}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes scrollLeft {
          0% { transform: translateX(0); }
          100% { transform: translateX(-25%); }
        }
      `}</style>

      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div
          className="flex w-max"
          style={{
            animation: `scrollLeft ${customers.length * 6}s linear infinite`,
            animationPlayState: paused ? 'paused' : 'running'
          }}
        >
          {items.map((customer, i) => (
            <CustomerCard key={`${customer.id}-${i}`} customer={customer} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HappyCustomers;
